import Delaunator from 'delaunator';
const FEET = 364000;

// A distance-limited triangulation supplies a concave survey footprint: long
// triangles across the infield, missing survey sections, and isolated runs are
// discarded. Values are interpolated before applying the display color scale.
export function buildSurface(samples, passWidthFeet = 4) {
  if (samples.length < 3) return null;
  const origin = samples[0].position;
  const scaleX = FEET * Math.cos(origin[1] * Math.PI / 180);
  const xy = samples.map(p => [(p.position[0] - origin[0]) * scaleX, (p.position[1] - origin[1]) * FEET]);
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x,y] of xy) { minX = Math.min(minX,x); maxX = Math.max(maxX,x); minY = Math.min(minY,y); maxY = Math.max(maxY,y); }
  const spanX = maxX-minX, spanY = maxY-minY;
  if (spanX < .01 || spanY < .01) return null;
  // Half-foot target cells, with a bounded raster for larger tracks. The
  // previous 2048 / 1.6M cap enlarged cells on full-track surveys and softened
  // local detail when the texture was magnified.
  const resolution = Math.max(.5, spanX/4096, spanY/4096, Math.sqrt(spanX*spanY/6400000));
  const width = Math.max(1,Math.ceil(spanX/resolution)), height = Math.max(1,Math.ceil(spanY/resolution));
  const dx = spanX/width, dy = spanY/height;
  const channels = samples[0].values.map((_, i) => samples.some(p => p.values[i] !== null) ? new Float32Array(width*height).fill(NaN) : null);
  const triangles = Delaunator.from(xy).triangles;
  const maxGapFeet = Math.min(30, Math.max(12, passWidthFeet*3));
  const maxEdge2 = maxGapFeet**2;
  let coveredPixels = 0;
  const covered = new Uint8Array(width*height);
  for (let t = 0; t < triangles.length; t += 3) {
    const ids = [triangles[t],triangles[t+1],triangles[t+2]];
    const [a,b,c] = ids.map(i => xy[i]);
    if ([ [a,b],[b,c],[c,a] ].some(([p,q]) => (p[0]-q[0])**2+(p[1]-q[1])**2 > maxEdge2)) continue;
    // Exclude triangles consisting solely of successive samples of one run.
    // They otherwise paint spurious slivers outside a curved outermost pass.
    if (ids.every(i => samples[i].segment === samples[ids[0]].segment) && Math.max(...ids.map(i => samples[i].station))-Math.min(...ids.map(i => samples[i].station)) < 25) continue;
    const denominator = (b[1]-c[1])*(a[0]-c[0])+(c[0]-b[0])*(a[1]-c[1]);
    if (Math.abs(denominator) < 1e-8) continue;
    const left = Math.max(0,Math.floor((Math.min(a[0],b[0],c[0])-minX)/dx));
    const right = Math.min(width-1,Math.floor((Math.max(a[0],b[0],c[0])-minX)/dx));
    const top = Math.max(0,Math.floor((maxY-Math.max(a[1],b[1],c[1]))/dy));
    const bottom = Math.min(height-1,Math.floor((maxY-Math.min(a[1],b[1],c[1]))/dy));
    for (let y = top; y <= bottom; y++) for (let x = left; x <= right; x++) {
      const px = minX+(x+.5)*dx, py = maxY-(y+.5)*dy;
      const wa = ((b[1]-c[1])*(px-c[0])+(c[0]-b[0])*(py-c[1]))/denominator;
      const wb = ((c[1]-a[1])*(px-c[0])+(a[0]-c[0])*(py-c[1]))/denominator;
      const wc = 1-wa-wb;
      if (Math.min(wa,wb,wc) < -1e-7) continue;
      const index = y*width+x;
      for (let ch = 0; ch < channels.length; ch++) {
        if (!channels[ch]) continue;
        const values = ids.map(i => samples[i].values[ch]);
        if (values.some(v => v === null || !Number.isFinite(v))) continue;
        channels[ch][index] = wa*values[0]+wb*values[1]+wc*values[2];
        if (!covered[index]) { covered[index] = 1; coveredPixels++; }
      }
    }
  }
  if (!coveredPixels) return null;
  return { width,height,channels,coveredPixels,maxGapFeet,resolutionFeet:resolution,
    bounds:[origin[0]+minX/scaleX,origin[1]+minY/FEET,origin[0]+maxX/scaleX,origin[1]+maxY/FEET] };
}

export function distanceToSurveyFeet(position, ribbons) {
  const sx = FEET*Math.cos(position[1]*Math.PI/180);
  let min = Infinity;
  for (const r of ribbons) {
    const end = [(r.polygon[1][0]+r.polygon[2][0])/2,(r.polygon[1][1]+r.polygon[2][1])/2];
    const ax = (r.position[0]-position[0])*sx, ay = (r.position[1]-position[1])*FEET;
    const bx = (end[0]-position[0])*sx, by = (end[1]-position[1])*FEET;
    const vx = bx-ax, vy = by-ay;
    const t = Math.max(0,Math.min(1,-(ax*vx+ay*vy)/(vx*vx+vy*vy || 1)));
    min = Math.min(min,Math.hypot(ax+t*vx,ay+t*vy));
  }
  return Number.isFinite(min) ? min : null;
}
